import qs from 'query-string';
import {Component} from 'react';
import {connect} from 'react-redux';
import {DefaultTimezone, Timezones} from '@deliveriesondemand/dod-data';

import DefaultLanding from '-/components/landing/Default';
import CompactLanding from '-/components/landing/Compact';
import DocumentsLanding from '-/components/landing/Documents';
import MeLanding from '-/components/landing/Me';
import NewOfferLanding from '-/components/landing/NewOffer';
import PaymentReductionLanding from '-/components/landing/PaymentReduction';
import RateReductionLanding from '-/components/landing/RateReduction';
import StipsLanding from '-/components/landing/Stips';
import GiftCardLongFormLanding from '-/components/landing/GiftCardLongFormLanding';
import UIService from '-/services/UIService';
import FunnelService from '-/services/FunnelService';
import NavigationService from '-/services/NavigationService';
import SessionService from '-/services/SessionService';
import {getStipsMode, isStipsMode} from '-/util/util';
import {get} from '-/util/lodash';
import {
    applicantStipObjectSelector,
    areThereStipsToResubmitSelector,
    allStipsVerfifiedForApplicantSelector,
    allStipsSubmittedForApplicantSelector,
    allStipsSubmittedSelector,
    applicantNameSelector,
    oppositeApplicantNameSelector
} from '-/selectors';

class Hero extends Component {
    componentDidMount() {
        const {step, validated} = this.props;

        // Session-scoped, and set before the first event rather than passed to each one.
        // Every event after this carries the arm the member was in, which is the only
        // way HERO_ADVANCED / step counts can be compared between landing pages.
        const {pageType, source} = UIService.getLandingPage();
        FunnelService.setVariant(pageType, source);

        const isCCRMode = SessionService.isCCRMode();
        const isCCRDomain = SessionService.isCCRDomain();
        const isCCRAuthenticated = SessionService.authenticated();

        // The hero is the initial redux step, so next('hero') is never called and
        // NavigationService never reports it. This is the only place it can be counted.
        // The redirects below are not a landing view, so record before deciding.
        const isRedirecting = (isCCRDomain && !isCCRMode)
            || (step === 'auth' && !validated)
            || (isCCRMode && isCCRDomain && isCCRAuthenticated && validated);

        if (!isRedirecting) {
            FunnelService.heroViewed();
        }

        if (isCCRDomain && !isCCRMode) {
            NavigationService.next('verifyAccountCCR');
        } else if (step === 'auth' && !validated) {
            UIService.setData({fromWorkflow: true});
            NavigationService.next('validationInfo');
        } else if (isCCRMode && isCCRDomain && isCCRAuthenticated && validated) {
            NavigationService.next('buyerInfo');
        }
    }

    render() {
        const {pageType, status} = this.props;
        const landingPageMap = {
            compact: CompactLanding,
            default: DefaultLanding,
            documents: DocumentsLanding,
            gclf: GiftCardLongFormLanding,
            gclf2: GiftCardLongFormLanding,
            me: MeLanding,
            new: NewOfferLanding,
            paymentReduction: PaymentReductionLanding,
            rateReduction: RateReductionLanding,
            stips: StipsLanding
        };

        // A member who has already submitted needs their status and the document
        // upload, not a headline about a rate they have already accepted. The default
        // page is the one that handles the submitted state, so the reduction pages hand
        // over to it rather than each growing a second mode.
        const isSubmitted = status === 'SUBMITTED';
        const handsOverWhenSubmitted = ['paymentReduction', 'rateReduction'];
        const LandingComponent = (isSubmitted && handsOverWhenSubmitted.includes(pageType))
            ? DefaultLanding
            : landingPageMap[pageType] || DefaultLanding;

        return <LandingComponent {...this.props} />;
    }
}

const mapStateToProps = (state = {}) => {
    const searchParams = qs.parse(location.search);
    const {
        authorized = '',
        authType = '',
        authInfo = '',
        step
    } = searchParams;
    const showBypassAuthButton = authorized && authType && authInfo;
    const pageType = UIService.getPageType();
    const {
        buyerInfo: buyerInfoState = {},
        creditor = {},
        dealInfo = {},
        documents = [],
        navigation = {},
        vehicleInfo = {}
    } = state;
    const validated = get(state, 'validationInfo.validated', false);
    const {buyerInfo = {}, buyerInfo_CoApp = {}} = buyerInfoState;
    const {shortName, name} = (getStipsMode() === 'coApp') ? buyerInfo_CoApp : buyerInfo;
    const rawTimezone = get(creditor, 'callback.timezone', DefaultTimezone);
    const timezone = Timezones[rawTimezone];
    const {appointmentDate} = dealInfo;
    const {currentStepLastSession} = navigation;
    const applicantName = applicantNameSelector(state);
    const oppositeApplicantName = oppositeApplicantNameSelector(state);
    const {coAppUnlocked} = state.navigation;
    const applicantType = get(state, 'stips.applicantType');

    let stips = {};
    if (isStipsMode()) {
        if (applicantType === 'coApp' && !coAppUnlocked) {
            throw new Error('Co-Applicant Stips', {
                cause: 'missingCoAppStips'
            });
        }

        const {requestStatus} = applicantStipObjectSelector(state);
        const allStipsVerifiedForApplicant = allStipsVerfifiedForApplicantSelector(state);
        const allStipsSubmittedForApplicant = allStipsSubmittedForApplicantSelector(state);
        const allStipsSubmitted = allStipsSubmittedSelector(state);
        const stipsToResubmit = areThereStipsToResubmitSelector(state);

        stips = {
            allStipsVerifiedForApplicant,
            allStipsSubmittedForApplicant,
            allStipsSubmitted,
            appointmentDate,
            requestStatus,
            stipsToResubmit
        };
    }

    return {
        auth: {
            authorized,
            authInfo,
            authType,
            showBypassAuthButton
        },
        creditor,
        currentStepLastSession,
        dealInfo,
        documents,
        pageType,
        applicantName,
        name,
        shortName,
        status: UIService.getStatus(),
        stips,
        vehicleInfo,
        oppositeApplicantName,
        coAppUnlocked,
        applicantType,
        step,
        timezone,
        validated
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onNextClick: () => {
            dispatch(() => {
                // Fired before the transition, with keepalive, so the navigation cannot
                // cancel it. This is the primary metric for landing-page experiments.
                FunnelService.heroAdvanced();

                UIService.stepCompleted('hero');

                let nextScreen = 'validationInfo';
                if (SessionService.isCCRMode()) {
                    nextScreen = 'verifyAccountCCR';
                }

                NavigationService.next(nextScreen);
            });
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Hero);
